// ========================================
// 📄 src/pages/AdminPage.jsx (AMÉLIORÉ)
// ========================================
import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { dashboardService } from "../services/dashboardService";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  TrendingUp,
  Award,
  LogOut,
  Home,
  BarChart3,
} from "lucide-react";
import StatCard from "../components/dashboard/StatCard";
import LoadingSpinner from "../components/common/LoadingSpinner";
import RevenueSummary from "../components/dashboard/RevenueSummary";
import RevenueAdminPanel from "../components/dashboard/RevenueAdminPanel";
import { useRevenue } from "../hooks/useRevenue";

const AdminPage = () => {
  const { isAdmin, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [topPerformers, setTopPerformers] = useState([]);
  const [loading, setLoading] = useState(true);
  const {
    globalTarget,
    teamProgress,
    userProgress,
    loading: revenueLoading,
    error: revenueError,
    updateGlobalTarget,
    updateObjectiveForUser,
    addObservation,
  } = useRevenue();

  useEffect(() => {
    if (isAdmin) {
      loadAdminData();
    }
  }, [isAdmin]);

  const loadAdminData = async () => {
    try {
      const [globalStats, performers] = await Promise.all([
        dashboardService.getGlobalStats(),
        dashboardService.getTopPerformers(),
      ]);

      setStats(globalStats);
      setTopPerformers(performers);
    } catch (error) {
      console.error("Erreur chargement données admin:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return <Navigate to="/dashboard" />;
  }

  if (loading || revenueLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/dashboard"
                className="text-gray-600 hover:text-gray-800"
              >
                <ArrowLeft className="w-6 h-6" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  Tableau de Bord Administrateur
                </h1>
                <p className="text-sm text-gray-600">
                  Vue d'ensemble de la plateforme
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Link
                to="/dashboard"
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Home className="w-5 h-5" />
                Dashboard
              </Link>
              <Link
                to="/leads"
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <BarChart3 className="w-5 h-5" />
                Prospects
              </Link>
              <button
                onClick={logout}
                className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <LogOut className="w-5 h-5" />
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Statistiques globales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={Users}
            title="Total Prospects"
            value={stats?.totalLeads || 0}
            subtitle="Tous les commerciaux"
            color="blue"
          />
          <StatCard
            icon={TrendingUp}
            title="Qualifiés"
            value={stats?.qualifiedLeads || 0}
            subtitle={`${stats?.contactedLeads || 0} contactés`}
            color="purple"
          />
          <StatCard
            icon={Award}
            title="Convertis"
            value={stats?.convertedLeads || 0}
            subtitle={`${
              stats?.totalLeads > 0
                ? ((stats.convertedLeads / stats.totalLeads) * 100).toFixed(1)
                : 0
            }% de conversion`}
            color="green"
          />
          <StatCard
            icon={Users}
            title="Nouveaux"
            value={stats?.newLeads || 0}
            subtitle="À traiter en priorité"
            color="yellow"
          />
        </div>

        {revenueError && (
          <div className="panel-quiet p-4 border border-red-200 bg-red-50 text-red-700 mb-8">
            Impossible de charger le suivi du chiffre d'affaires. Réessayez plus
            tard.
          </div>
        )}

        <RevenueSummary
          isAdmin
          globalTarget={globalTarget}
          userObjective={null}
          progress={userProgress}
          teamProgress={teamProgress}
          latestObservation={null}
        />

        <RevenueAdminPanel
          globalTarget={globalTarget}
          teamProgress={teamProgress}
          onSaveTarget={updateGlobalTarget}
          onSaveObjective={updateObjectiveForUser}
          onAddObservation={addObservation}
        />

        {/* Top Performers */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold">
              🏆 Classement des Commerciaux
            </h3>
            <span className="text-sm text-gray-500">
              Basé sur le taux de conversion
            </span>
          </div>

          {topPerformers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucun commercial pour le moment</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">
                      Rang
                    </th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">
                      Commercial
                    </th>
                    <th className="text-center py-4 px-4 font-semibold text-gray-700">
                      Total Prospects
                    </th>
                    <th className="text-center py-4 px-4 font-semibold text-gray-700">
                      Convertis
                    </th>
                    <th className="text-center py-4 px-4 font-semibold text-gray-700">
                      Taux Conversion
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {topPerformers.map((performer, index) => (
                    <tr
                      key={performer.userId}
                      className={`border-b hover:bg-gray-50 transition-colors ${
                        index === 0 ? "bg-yellow-50" : ""
                      }`}
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          {index === 0 && <span className="text-2xl">🥇</span>}
                          {index === 1 && <span className="text-2xl">🥈</span>}
                          {index === 2 && <span className="text-2xl">🥉</span>}
                          <span className="font-bold text-lg text-gray-700">
                            #{index + 1}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="font-medium text-gray-800">
                          {performer.name}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="text-gray-700 font-medium">
                          {performer.totalLeads}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                          {performer.converted}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full transition-all"
                              style={{ width: `${performer.conversionRate}%` }}
                            />
                          </div>
                          <span className="font-bold text-blue-600">
                            {performer.conversionRate}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Actions rapides */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <Link
            to="/leads"
            className="card hover:shadow-lg transition-shadow cursor-pointer"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">
                  Tous les Prospects
                </h4>
                <p className="text-sm text-gray-600">Gérer et filtrer</p>
              </div>
            </div>
          </Link>

          <Link
            to="/leads/add"
            className="card hover:shadow-lg transition-shadow cursor-pointer"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">
                  Ajouter un Prospect
                </h4>
                <p className="text-sm text-gray-600">Nouveau lead</p>
              </div>
            </div>
          </Link>

          <Link
            to="/dashboard"
            className="card hover:shadow-lg transition-shadow cursor-pointer"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Home className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Mon Dashboard</h4>
                <p className="text-sm text-gray-600">Vue personnelle</p>
              </div>
            </div>
          </Link>
        </div>
      </main>
    </div>
  );
};

export default AdminPage;
